import React, { useMemo, useState } from 'react';
import { IconCheck, IconRefresh, IconTrash, IconX } from '../Icons';
import {
  snaptradeConnectLink,
  snaptradeSync,
  snaptradeRemoveConnection,
  snaptradeDisconnectAll,
} from '../../services/vectorDashboardApi';
import './SnaptradeConnectPanel.css';

/**
 * Link Schwab, Robinhood, Webull and other brokerages through SnapTrade.
 *
 * "Link a brokerage" opens SnapTrade's Connection Portal; the user signs in on
 * their broker's own page and comes back to My Portfolio, where the sync runs.
 * Read-only: RiskLo sees balances, positions and activity, never places trades.
 */

const POPULAR = ['Charles Schwab', 'Robinhood', 'Webull', 'Fidelity', 'E*TRADE', 'Vanguard', 'Public', 'tastytrade'];

const money = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: Math.abs(n) < 100 ? 2 : 0,
}));

function timeAgo(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

export default function SnaptradeConnectPanel({ status = {}, onBack, onClose, onChanged, returnTo = null }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const available = status.available !== false;
  const accounts = useMemo(() => status.accounts || [], [status.accounts]);

  // Accounts grouped by brokerage connection.
  const connections = useMemo(() => {
    const map = new Map();
    accounts.forEach((a) => {
      const key = a.connectionId || a.accountId;
      if (!map.has(key)) map.set(key, { id: a.connectionId, institution: a.institution || 'Brokerage', disabled: false, accounts: [] });
      const c = map.get(key);
      c.accounts.push(a);
      if (a.disabled) c.disabled = true;
    });
    return [...map.values()];
  }, [accounts]);
  const hasSnapshotOnly = accounts.some((a) => a.historySource === 'snapshot');

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    setMessage('');
    try {
      await fn();
    } catch (e) {
      setError(e.message || 'Something went wrong. Try again.');
    } finally {
      setBusy('');
    }
  };

  const openPortal = (reconnect = null) => run(reconnect ? `re-${reconnect}` : 'link', async () => {
    const light = typeof document !== 'undefined' && document.body.classList.contains('light-mode');
    const res = await snaptradeConnectLink({ reconnect, darkMode: !light, returnTo });
    if (!res?.url) throw new Error('Couldn’t open the brokerage sign-in. Try again.');
    window.location.assign(res.url);
  });

  const sync = () => run('sync', async () => {
    const res = await snaptradeSync();
    setMessage(res.message || 'Synced.');
    onChanged?.();
  });

  const remove = (c) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Unlink ${c.institution}? Its ${c.accounts.length === 1 ? 'account' : `${c.accounts.length} accounts`} and their history leave RiskLo.`)) return;
    run(`rm-${c.id}`, async () => {
      await snaptradeRemoveConnection(c.id);
      setMessage(`${c.institution} unlinked.`);
      onChanged?.();
    });
  };

  const removeAll = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Unlink every brokerage linked through SnapTrade and delete their history from RiskLo?')) return;
    run('all', async () => {
      await snaptradeDisconnectAll();
      setMessage('All brokerages unlinked.');
      onChanged?.();
    });
  };

  return (
    <div className="st-panel">
      <header className="vd-broker-detail-header st-head">
        <button type="button" className="vd-back-btn" onClick={onBack}>← All brokers</button>
        <button type="button" className="vd-modal-close" onClick={onClose} aria-label="Close"><IconX size={18} /></button>
      </header>

      <section className="st-hero">
        <span className="st-hero-mark" aria-hidden="true">
          <span>S</span><span>R</span><span>W</span><span>+</span>
        </span>
        <div>
          <h3>Schwab, Robinhood, Webull &amp; more</h3>
          <p>
            Link your brokerage in a few clicks. You sign in on your broker&rsquo;s own page, so RiskLo never sees your
            password. <strong>Read-only:</strong> RiskLo sees balances, positions and trades, and can&rsquo;t place orders.
          </p>
        </div>
      </section>

      <div className="st-chips" aria-label="Popular brokerages">
        {POPULAR.map((b) => <span key={b} className="st-chip">{b}</span>)}
        <span className="st-chip st-chip--more">and more</span>
      </div>

      {available ? (
        <button type="button" className="st-link-btn" onClick={() => openPortal()} disabled={Boolean(busy)}>
          {busy === 'link' ? 'Opening…' : connections.length ? '+ Link another brokerage' : 'Link a brokerage'}
        </button>
      ) : (
        <div className="st-soon">
          <strong>Coming soon</strong>
          <span>Brokerage linking is being switched on. Check back shortly.</span>
        </div>
      )}

      {error ? <p className="st-error" role="alert">{error}</p> : null}
      {message ? <p className="st-message" role="status"><IconCheck size={14} /> {message}</p> : null}

      {connections.length ? (
        <section className="st-linked">
          <div className="st-linked-head">
            <h4>Linked brokerages</h4>
            <button type="button" className="st-ghost-btn" onClick={sync} disabled={Boolean(busy)}>
              <IconRefresh size={14} /> {busy === 'sync' ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
          {connections.map((c) => (
            <div key={c.id || c.institution} className={`st-conn${c.disabled ? ' is-broken' : ''}`}>
              <div className="st-conn-top">
                <span className="st-conn-mark" aria-hidden="true">{c.institution.trim().charAt(0).toUpperCase()}</span>
                <strong>{c.institution}</strong>
                {c.disabled ? <span className="st-badge st-badge--warn">Needs reconnecting</span> : <span className="st-badge">Connected</span>}
                <span className="st-conn-actions">
                  {c.disabled && c.id ? (
                    <button type="button" className="st-ghost-btn" onClick={() => openPortal(c.id)} disabled={Boolean(busy)}>
                      {busy === `re-${c.id}` ? 'Opening…' : 'Reconnect'}
                    </button>
                  ) : null}
                  {c.id ? (
                    <button type="button" className="st-icon-btn" onClick={() => remove(c)} disabled={Boolean(busy)} aria-label={`Unlink ${c.institution}`} title="Unlink">
                      <IconTrash size={14} />
                    </button>
                  ) : null}
                </span>
              </div>
              <ul className="st-accounts">
                {c.accounts.map((a) => (
                  <li key={a.accountId}>
                    <span>{a.name || 'Account'}{a.last4 ? ` ••${a.last4}` : ''}</span>
                    <b>{money(a.totalValue)}</b>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="st-foot">
            <span>
              {status.lastSyncAt ? `Last synced ${timeAgo(status.lastSyncAt)}. ` : ''}
              Refreshes automatically several times a day.
              {hasSnapshotOnly ? ' Daily value history builds up from the day you link.' : ''}
            </span>
            <button type="button" className="st-text-btn" onClick={removeAll} disabled={Boolean(busy)}>Unlink all</button>
          </div>
        </section>
      ) : null}

      <p className="st-powered">
        Brokerage connections by SnapTrade. Daily account values go back up to 12 months before you link
        (SnapTrade&rsquo;s limit) and keep building every day after. Trades and activity can go back further,
        depending on your broker.
      </p>
    </div>
  );
}
